import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconLanguage,
  IconPlus,
  IconSubtitlesEdit,
  IconX,
} from "@tabler/icons-react";
import "@assets/css/start_point/panel.css";
import "@assets/css/mode_tool/panel.css";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import {
  createStartPointRequest,
  deleteStartPointRequest,
  updateStartPointRequest,
} from "@services/startPoints/startPointsApi";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import {
  buildStartPointDescriptionPayload,
  buildStartPointDescriptionTranslationPayload,
  buildStartPointModel,
  buildStartPointLocalizedPayload,
  buildStartPointLocalizedTranslationPayload,
  normalizeStartPointHtmlValue,
  normalizeStartPointLocalizedValue,
  pickStartPointTourName,
  resolveStartPointTourId,
} from "@utils/start_point/panelUtils";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import StartPointAddUpdate from "@components/start_point/addUpdate";
import StartPointDescriptionEditor from "@components/start_point/descriptionEditor";
import {
  StartPointDeleteButton,
  StartPointDeleteModal,
} from "@components/start_point/delete";
import StartPointModeToolAction from "@components/start_point/modeToolAction";
import {
  StartPointPromoCodesButton,
  StartPointPromoCodesModal,
} from "@components/start_point/promoCodes";
import AppModal from "@components/modals/AppModal";
import MediaPicker from "@components/media/MediaPicker";
import AiToolsPanel from "@components/ai_tools/AiToolsPanel";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import { translateGenericStream } from "@services/ai/aiApi";
import "@assets/css/mode_tool/translatePointsModal.css";

const MAX_ADDITIONAL_INFO_IMAGES = 10;

function normalizeAdditionalInfoImages(images) {
  return (Array.isArray(images) ? images : images ? [images] : [])
    .filter((image) => image && (image.id || image.url))
    .slice(0, MAX_ADDITIONAL_INFO_IMAGES);
}

function StartPointToolPanel({
  startPointMode,
  activeStartTour,
  startTourId,
  startPoints,
  setStartPoints,
  languages,
  loadingStartPoints,
  accordionResetKey,
  autoExpandPointId,
  isPickingStartPoints,
  onTogglePickingStartPoints,
  onSelectStartPoint,
  onAddDrivingModePoint,
  onFetchAllTourPoints,
  onClearRoutePreview,
  onFocusStartPoint,
  onRemoveStartPoint,
  onRefreshStartPoints,
  onExitStartPointMode,
}) {
  const [expandedPointId, setExpandedPointId] = useState("");
  const aiToolsAnchorRef = useRef(null);
  const [aiToolsAnchorReady, setAiToolsAnchorReady] = useState(false);
  const languageList = useStartPointLanguages(languages);
  const activeStartTourKey = resolveStartPointTourId(activeStartTour);
  const [editor, setEditor] = useState({
    isOpen: false,
    pointId: "",
    languageIso: "",
    field: "description",
  });
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [savingPointId, setSavingPointId] = useState("");
  const [uploadingBannerByPointId, setUploadingBannerByPointId] = useState({});
  const [bannerErrorByPointId, setBannerErrorByPointId] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingStartPoint, setDeletingStartPoint] = useState(false);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [promoCodesPointId, setPromoCodesPointId] = useState("");
  const [additionalInfoPointId, setAdditionalInfoPointId] = useState("");
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateComplete, setTranslateComplete] = useState(false);
  const [translateError, setTranslateError] = useState("");
  const [translateProgress, setTranslateProgress] = useState({
    completed: 0,
    total: 0,
    done: false,
    points: [],
  });
  const [
    uploadingAdditionalInfoImageByPointId,
    setUploadingAdditionalInfoImageByPointId,
  ] = useState({});
  const [
    additionalInfoImageErrorByPointId,
    setAdditionalInfoImageErrorByPointId,
  ] = useState({});

  useEffect(() => {
    const isos = languageList.map((language) => language.iso).filter(Boolean);
    if (!isos.length || typeof setStartPoints !== "function") return;
    setStartPoints((currentPoints) =>
      (Array.isArray(currentPoints) ? currentPoints : []).map((point) => {
        const nextDescription = { ...(point?.description || {}) };
        const nextName = { ...(point?.name || {}) };
        isos.forEach((iso) => {
          if (typeof nextDescription[iso] === "undefined") nextDescription[iso] = "";
          if (typeof nextName[iso] === "undefined") nextName[iso] = "";
        });
        return { ...point, description: nextDescription, name: nextName };
      }),
    );
  }, [languageList, setStartPoints]);

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    const englishLanguage = languageList.find(
      (language) => language.iso.trim().split(/[-_]/)[0] === "en",
    );
    setActiveLanguageIso(englishLanguage?.iso || languageList[0].iso);
  }, [activeStartTourKey, languageList]);

  useLayoutEffect(() => {
    if (activeStartTour && aiToolsAnchorRef.current) {
      setAiToolsAnchorReady(true);
      return;
    }
    setAiToolsAnchorReady(false);
  }, [activeStartTour, activeStartTourKey, startPointMode]);

  const hasResolvedActiveLanguage = languageList.some(
    (language) => language.iso === activeLanguageIso,
  );
  const visibleLanguageIso = hasResolvedActiveLanguage
    ? activeLanguageIso
    : "";
  const isEnglishLanguage =
    visibleLanguageIso.toLowerCase().trim().split(/[-_]/)[0] === "en";
  const translationLanguages = useMemo(
    () => languageList.filter(
      (language) => language.iso.toLowerCase().trim().split(/[-_]/)[0] !== "en",
    ),
    [languageList],
  );
  const translationPercent = translateProgress.total
    ? Math.min(
        100,
        Math.round(
          (translateProgress.completed / translateProgress.total) * 100,
        ),
      )
    : 0;

  useEffect(() => {
    setExpandedPointId("");
  }, [accordionResetKey]);

  useEffect(() => {
    if (!autoExpandPointId) return;
    setExpandedPointId(autoExpandPointId);
  }, [autoExpandPointId]);

  function openDescriptionEditor(
    pointId,
    languageIso,
    field = "description",
  ) {
    const point = startPoints.find((item) => item.id === pointId);
    setEditor({ isOpen: true, pointId, languageIso, field });
    setDescriptionHtml(
      field === "additionalInfoDescription"
        ? normalizeStartPointHtmlValue(
            point?.additionalInfo?.description?.[languageIso],
          )
        : normalizeStartPointHtmlValue(point?.description?.[languageIso]),
    );
  }

  function openTranslateModal() {
    setTranslateLanguage(translationLanguages[0]?.iso || "");
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({ completed: 0, total: 0, done: false, points: [] });
    setIsTranslateModalOpen(true);
  }

  async function handleTranslateStartPoints() {
    if (!translateLanguage || !Array.isArray(startPoints) || !startPoints.length) {
      return;
    }

    const records = startPoints.map((point, index) => {
      const pointId = String(
        point?.id || point?.internalId || point?.stp_id || `start-point-${index + 1}`,
      );
      const localizedEntry = (entry, normalize) => ({
        id: String(entry && typeof entry === "object" ? entry.id || "" : ""),
        value: String(normalize(entry) || ""),
      });
      return {
        id: pointId,
        name: localizedEntry(
          point?.name?.en,
          normalizeStartPointLocalizedValue,
        ),
        description: localizedEntry(
          point?.description?.en,
          normalizeStartPointHtmlValue,
        ),
        additional_info: {
          name: localizedEntry(
            point?.additionalInfo?.name?.en,
            normalizeStartPointLocalizedValue,
          ),
          description: localizedEntry(
            point?.additionalInfo?.description?.en,
            normalizeStartPointHtmlValue,
          ),
        },
      };
    });
    const statusById = new Map(
      records.map((record) => [record.id, { status: "pending", message: "" }]),
    );

    setIsTranslating(true);
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({
      completed: 0,
      total: records.length,
      done: false,
      points: records.map((record, index) => ({
        id: record.id,
        name: record.name.value || `Start point ${index + 1}`,
        status: "pending",
        message: "",
      })),
    });

    try {
      const response = await translateGenericStream(
        { language: translateLanguage, data: records },
        ({ event, data }) => {
          if (event !== "point") return;
          const id = String(data?.id || "");
          if (!statusById.has(id)) return;
          const status = data?.status === "error" ? "error" : "completed";
          statusById.set(id, {
            status,
            message: status === "error" ? data?.message || "Translation failed." : "",
          });
          const completed = [...statusById.values()].filter(
            (item) => item.status !== "pending",
          ).length;
          setTranslateProgress((current) => ({
            ...current,
            completed,
            points: current.points.map((item) =>
              item.id === id ? { ...item, ...statusById.get(id) } : item,
            ),
          }));
        },
      );
      const translatedRecords = response?.body?.data;
      if (response?.error || !Array.isArray(translatedRecords)) {
        throw new Error(
          response?.message || "The translation response was incomplete.",
        );
      }

      const translatedById = new Map(
        translatedRecords.map((record) => [String(record?.id || ""), record]),
      );
      const failedIds = new Set(
        [...statusById.entries()]
          .filter(([, result]) => result.status === "error")
          .map(([id]) => id),
      );
      const translatedField = (current, translated) => ({
        ...(current && typeof current === "object" ? current : {}),
        value: String(translated?.value ?? ""),
      });

      setStartPoints((currentPoints) =>
        (Array.isArray(currentPoints) ? currentPoints : []).map((point, index) => {
          const id = String(
            point?.id || point?.internalId || point?.stp_id || `start-point-${index + 1}`,
          );
          const translated = translatedById.get(id);
          if (!translated || failedIds.has(id)) return point;
          return {
            ...point,
            name: {
              ...(point?.name || {}),
              [translateLanguage]: translatedField(
                point?.name?.[translateLanguage],
                translated.name,
              ),
            },
            description: {
              ...(point?.description || {}),
              [translateLanguage]: translatedField(
                point?.description?.[translateLanguage],
                translated.description,
              ),
            },
            additionalInfo: {
              ...(point?.additionalInfo || {}),
              name: {
                ...(point?.additionalInfo?.name || {}),
                [translateLanguage]: translatedField(
                  point?.additionalInfo?.name?.[translateLanguage],
                  translated?.additional_info?.name,
                ),
              },
              description: {
                ...(point?.additionalInfo?.description || {}),
                [translateLanguage]: translatedField(
                  point?.additionalInfo?.description?.[translateLanguage],
                  translated?.additional_info?.description,
                ),
              },
            },
          };
        }),
      );

      const hasErrors = [...statusById.values()].some(
        (result) => result.status === "error",
      );
      setTranslateComplete(!hasErrors);
      if (hasErrors) {
        setTranslateError(
          "Some points could not be translated. Review the errors below.",
        );
      }
      setTranslateProgress((current) => ({ ...current, done: true }));
    } catch (error) {
      setTranslateError(error?.message || "Could not translate driving tour points.");
      setTranslateProgress((current) => ({ ...current, done: true }));
    } finally {
      setIsTranslating(false);
    }
  }

  function closeDescriptionEditor() {
    setEditor({
      isOpen: false,
      pointId: "",
      languageIso: "",
      field: "description",
    });
    setDescriptionHtml("");
    setIsDescriptionEditorReady(false);
  }

  function saveDescriptionEditor() {
    if (typeof setStartPoints === "function") {
      setStartPoints((currentPoints) =>
        (Array.isArray(currentPoints) ? currentPoints : []).map((point) => {
          if (point.id !== editor.pointId) return point;
          const isAdditionalInfo =
            editor.field === "additionalInfoDescription";
          const currentValue = isAdditionalInfo
            ? point?.additionalInfo?.description?.[editor.languageIso]
            : point?.description?.[editor.languageIso];
          if (isAdditionalInfo) {
            return {
              ...point,
              additionalInfo: {
                ...(point?.additionalInfo || {}),
                description: {
                  ...(point?.additionalInfo?.description || {}),
                  [editor.languageIso]:
                    currentValue && typeof currentValue === "object"
                      ? { ...currentValue, value: descriptionHtml ?? "" }
                      : descriptionHtml ?? "",
                },
              },
            };
          }
          return {
            ...point,
            description: {
              ...(point?.description || {}),
              [editor.languageIso]:
                currentValue && typeof currentValue === "object"
                  ? { ...currentValue, value: descriptionHtml ?? "" }
                  : descriptionHtml ?? "",
            },
          };
        }),
      );
    }
    closeDescriptionEditor();
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: descriptionHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setDescriptionHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsDescriptionEditorReady(true);
  }, [descriptionHtml, editor.isOpen, quillHost]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsDescriptionEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  if (!activeStartTour) return null;

  const promoCodesPoint =
    startPoints.find((item) => item.id === promoCodesPointId) || null;
  const additionalInfoPoint =
    startPoints.find((item) => item.id === additionalInfoPointId) || null;
  const additionalInfoImages = normalizeAdditionalInfoImages(
    additionalInfoPoint?.additionalInfo?.image,
  );

  async function handleSaveStartPoint(point) {
    const resolvedTourId = startTourId || resolveStartPointTourId(activeStartTour);
    if (!resolvedTourId) return;

    const pointNamePayload = buildStartPointLocalizedPayload(
      point.name,
      languageList,
    );
    const additionalDescriptionPointPayload = {
      name: buildStartPointLocalizedTranslationPayload(
        point?.additionalInfo?.name,
        languageList,
      ),
      description: buildStartPointDescriptionTranslationPayload(
        point?.additionalInfo?.description,
        languageList,
      ),
      image: normalizeAdditionalInfoImages(point?.additionalInfo?.image).map(
        (image) => ({
          id: image.id || "",
          url: image.url || "",
        }),
      ),
    };
    const payload = {
      id: point.id,
      tourId: resolvedTourId,
      order: point.order,
      longitude: point.longitude,
      latitude: point.latitude,
      name: pointNamePayload,
      status: Number(point.status) || 1,
      description: buildStartPointDescriptionPayload(
        point.description,
        languageList,
      ),
      banner: point?.banner?.id || null,
      price: {
        currencyIso: point?.price?.currencyIso || "USD",
        amount: Number(point?.price?.amount) || 0,
      },
      description_point: additionalDescriptionPointPayload,
      AppleAppStoreID: String(point?.appleAppStoreId || ""),
      GooglePlayStoreID: String(point?.googlePlayStoreId || ""),
    };

    setSavingPointId(point.id);
    // Detects create vs update to decide whether we should force a backend list refresh.
    const isExistingPoint = Boolean(
      point?.isPersisted || point?.internalId || point?.stp_id || point?.stpId,
    );
    try {
      const response = isExistingPoint
        ? await updateStartPointRequest(point.id, payload)
        : await createStartPointRequest(payload);
      const serverPoint = response?.body?.startPoint || null;
      if (serverPoint && typeof setStartPoints === "function") {
        setStartPoints((currentPoints) =>
          (Array.isArray(currentPoints) ? currentPoints : []).map((currentPoint, index) =>
            currentPoint.id === point.id
              ? buildStartPointModel(
                  {
                    ...currentPoint,
                    ...serverPoint,
                    id: serverPoint.id || serverPoint.stp_code || currentPoint.id,
                    uuid: serverPoint.uuid || currentPoint.uuid || "",
                    internalId:
                      serverPoint.stp_id ||
                      serverPoint.uuid ||
                      serverPoint.id ||
                      currentPoint.internalId ||
                      "",
                    isPersisted: true,
                  },
                  index,
                )
              : currentPoint,
          ),
        );
      }
      // After creating a new driving tour points, refresh list/map from backend source of truth.
      if (!isExistingPoint) {
        await onRefreshStartPoints?.();
      }
    } catch {
    } finally {
      setSavingPointId("");
    }
  }

  async function handleUploadPointBanner(pointId, files) {
    if (!Array.isArray(files) || !files.length) return;
    setUploadingBannerByPointId((prev) => ({ ...prev, [pointId]: true }));
    setBannerErrorByPointId((prev) => ({ ...prev, [pointId]: "" }));
    try {
      const response = await createMultimediaRequest({ file: files[0] });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not create banner.");
      const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
      if (typeof setStartPoints === "function") {
        setStartPoints((currentPoints) =>
          (Array.isArray(currentPoints) ? currentPoints : []).map((point) =>
            point.id === pointId
              ? {
                  ...point,
                  banner: {
                    id: rawItem?.id || rawItem?.mmd_id || "",
                    url: resolveMediaUrl(rawUrl),
                    mediaType:
                      rawItem?.mediaType ||
                      rawItem?.mmd_media_type ||
                      inferMediaType(rawUrl),
                    mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
                  },
                }
              : point,
          ),
        );
      }
    } catch (error) {
      setBannerErrorByPointId((prev) => ({
        ...prev,
        [pointId]: error?.message || "Could not upload banner.",
      }));
    } finally {
      setUploadingBannerByPointId((prev) => ({ ...prev, [pointId]: false }));
    }
  }

  async function handleUploadAdditionalInfoImage(pointId, files) {
    if (!Array.isArray(files) || !files.length) return;
    const currentPoint = startPoints.find((point) => point.id === pointId);
    const currentImages = normalizeAdditionalInfoImages(
      currentPoint?.additionalInfo?.image,
    );
    const availableSlots = Math.max(
      0,
      MAX_ADDITIONAL_INFO_IMAGES - currentImages.length,
    );
    const filesToUpload = files.slice(0, availableSlots);
    if (!filesToUpload.length) {
      setAdditionalInfoImageErrorByPointId((prev) => ({
        ...prev,
        [pointId]: `Maximum ${MAX_ADDITIONAL_INFO_IMAGES} images are allowed.`,
      }));
      return;
    }
    setUploadingAdditionalInfoImageByPointId((prev) => ({
      ...prev,
      [pointId]: true,
    }));
    setAdditionalInfoImageErrorByPointId((prev) => ({
      ...prev,
      [pointId]: "",
    }));
    try {
      const uploadedImages = [];
      for (const file of filesToUpload) {
        const response = await createMultimediaRequest({ file });
        const body = response?.body || {};
        const rawItem = Array.isArray(body?.multimediaList)
          ? body.multimediaList[0]
          : Array.isArray(body?.multimedia)
            ? body.multimedia[0]
            : body?.multimedia || null;
        if (!rawItem) throw new Error("Could not create image.");
        const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
        uploadedImages.push({
          id: rawItem?.id || rawItem?.mmd_id || "",
          url: resolveMediaUrl(rawUrl),
          mediaType:
            rawItem?.mediaType ||
            rawItem?.mmd_media_type ||
            inferMediaType(rawUrl),
          mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
        });
      }
      setStartPoints?.((currentPoints) =>
        (Array.isArray(currentPoints) ? currentPoints : []).map((point) =>
          point.id === pointId
            ? (() => {
                const nextImages = [
                  ...normalizeAdditionalInfoImages(point?.additionalInfo?.image),
                  ...uploadedImages,
                ].slice(0, MAX_ADDITIONAL_INFO_IMAGES);
                return {
                  ...point,
                  additionalInfo: {
                    ...(point?.additionalInfo || {}),
                    image: nextImages,
                  },
                };
              })()
            : point,
        ),
      );
    } catch (error) {
      setAdditionalInfoImageErrorByPointId((prev) => ({
        ...prev,
        [pointId]: error?.message || "Could not upload image.",
      }));
    } finally {
      setUploadingAdditionalInfoImageByPointId((prev) => ({
        ...prev,
        [pointId]: false,
      }));
    }
  }

  function updateAdditionalInfoName(pointId, languageIso, value) {
    setStartPoints?.((currentPoints) =>
      (Array.isArray(currentPoints) ? currentPoints : []).map((point) =>
        point.id === pointId
          ? (() => {
              const currentValue = point?.additionalInfo?.name?.[languageIso];
              return {
                ...point,
                additionalInfo: {
                  ...(point?.additionalInfo || {}),
                  name: {
                    ...(point?.additionalInfo?.name || {}),
                    [languageIso]:
                      currentValue && typeof currentValue === "object"
                        ? { ...currentValue, value }
                        : value,
                  },
                },
              };
            })()
          : point,
      ),
    );
  }

  function removeAdditionalInfoImage(pointId, imageId) {
    setStartPoints?.((currentPoints) =>
      (Array.isArray(currentPoints) ? currentPoints : []).map((point) =>
        point.id === pointId
          ? {
              ...point,
              additionalInfo: {
                ...(point?.additionalInfo || {}),
                image: normalizeAdditionalInfoImages(
                  point?.additionalInfo?.image,
                ).filter((image) => (image.id || image.url) !== imageId),
              },
            }
          : point,
      ),
    );
  }

  function handleRequestDelete(point, index) {
    setPendingDelete({ point, index });
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    try {
      setDeletingStartPoint(true);
      const point = pendingDelete.point || {};
      const isExistingPoint = Boolean(
        point?.isPersisted ||
        point?.internalId ||
        point?.stp_id ||
        point?.stpId,
      );
      const deleteId =
        point?.internalId || point?.stp_id || point?.stpId || point?.id;
      if (isExistingPoint && deleteId) {
        await deleteStartPointRequest(deleteId);
      }
      if (typeof setStartPoints === "function") {
        setStartPoints((currentPoints) =>
          (Array.isArray(currentPoints) ? currentPoints : [])
            .filter((item) => item.id !== pendingDelete.point.id)
            .map((item, index) => ({ ...item, order: index + 1 })),
        );
      }
      onRemoveStartPoint?.(pendingDelete.point.id);
      if (expandedPointId === pendingDelete.point.id) setExpandedPointId("");
      setPendingDelete(null);
    } catch {
    } finally {
      setDeletingStartPoint(false);
    }
  }

  return (
    <aside
      className="start-point-tool-overlay"
      ref={aiToolsAnchorRef}
    >
      <div className="mode-route-tool-header start-point-tool-header">
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${visibleLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveLanguageIso(language.iso);
                  }}
                  aria-pressed={visibleLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mode-route-tool-head">
          <button
            type="button"
            className="app-icon-btn app-icon-btn--back"
            aria-label="Go back"
            title="Go back"
            onClick={() => {
              onClearRoutePreview?.();
              onExitStartPointMode?.();
            }}
          >
            <IconArrowLeft size={16} />
          </button>
          {startPointMode ? (
            <button
              type="button"
              className="app-icon-btn app-icon-btn--update"
              aria-label={
                isPickingStartPoints
                  ? "Browse map"
                  : "Select new driving tour points"
              }
              title={
                isPickingStartPoints
                  ? "Cancel add start point"
                  : "Add start point"
              }
              onClick={() => {
                setExpandedPointId("");
                onSelectStartPoint?.(null);
                onClearRoutePreview?.();
                onTogglePickingStartPoints?.();
              }}
            >
              {isPickingStartPoints ? <IconX size={16} /> : <IconPlus size={16} />}
            </button>
          ) : null}
          <h3 className="mode-route-tool-title start-point-tool-title">
            Route - {pickStartPointTourName(activeStartTour)}
            <p className="mode-route-tool-subtitle">driving tour points</p>
          </h3>
        </div>
      </div>

      <section className="start-point-tool-panel">
        <p className="start-point-tool-help">
          Tour: <b>{pickStartPointTourName(activeStartTour)}</b>. Click on the
          map to add points or drag markers to adjust.
        </p>
        {loadingStartPoints ? (
          <p className="start-point-tool-help">
            Loading driving tour points...
          </p>
        ) : null}
        {!loadingStartPoints &&
        Array.isArray(startPoints) &&
        startPoints.length ? (
          <div className="start-point-tool-list">
            {startPoints.map((point, index) => {
              const expandBg = expandedPointId === point.id ? "open" : "";

              return (
                <div
                  key={point.id || `point-${index + 1}`}
                  className={`start-point-tool-item ${expandBg}`}
                >
                  <div className="start-point-tool-item-head">
                    <button
                      type="button"
                      className="start-point-tool-accordion"
                      onClick={() => {
                        onAddDrivingModePoint?.(point);
                      }}
                    >
                      {normalizeStartPointLocalizedValue(
                        point?.name?.[visibleLanguageIso],
                      ) ||
                        normalizeStartPointLocalizedValue(point?.name?.en) ||
                        ""}
                    </button>

                    <div className="start-point-tool-item-actions">
                      {expandedPointId === point.id ? (
                        <button
                          type="button"
                          className="app-icon-btn app-icon-btn--update start-point-tool-additional-info-action"
                          aria-label="Enter more information about the description"
                          title="Enter more information about the description"
                          onClick={() => setAdditionalInfoPointId(point.id)}
                        >
                          <IconSubtitlesEdit size={14} />
                        </button>
                      ) : null}
                      <StartPointModeToolAction
                        onClick={() => {
                          const nextExpandedId =
                            expandedPointId === point.id ? "" : point.id;
                          setExpandedPointId(nextExpandedId);
                          onSelectStartPoint?.(nextExpandedId ? point : null);
                          if (!nextExpandedId) {
                            onClearRoutePreview?.();
                            return;
                          }
                          const tour_id =
                            resolveStartPointTourId(activeStartTour);
                          onFetchAllTourPoints?.(
                            null,
                            tour_id,
                            "driving",
                            point?.tourTypeId,
                            nextExpandedId ? point?.id : "",
                          );
                          if (
                            nextExpandedId &&
                            typeof onFocusStartPoint === "function"
                          )
                            onFocusStartPoint(point);
                        }}
                      />
                      <StartPointPromoCodesButton
                        onClick={() => setPromoCodesPointId(point.id)}
                      />
                      <StartPointDeleteButton
                        order={point.order || index + 1}
                        onClick={() => handleRequestDelete(point, index)}
                      />
                    </div>
                  </div>

                  {expandedPointId === point.id ? (
                    <StartPointAddUpdate
                      point={point}
                      languageList={languageList}
                      activeLanguageIso={visibleLanguageIso}
                      setStartPoints={setStartPoints}
                      uploadingBannerByPointId={uploadingBannerByPointId}
                      bannerErrorByPointId={bannerErrorByPointId}
                      setBannerErrorByPointId={setBannerErrorByPointId}
                      onOpenDescriptionEditor={openDescriptionEditor}
                      onUploadPointBanner={handleUploadPointBanner}
                      onSaveStartPoint={handleSaveStartPoint}
                      savingPointId={savingPointId}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
        {!loadingStartPoints &&
        (!Array.isArray(startPoints) || !startPoints.length) ? (
          <p className="start-point-tool-help">
            This tour has no driving tour points yet.
          </p>
        ) : null}
      </section>

      <StartPointDescriptionEditor
        editor={editor}
        isDescriptionEditorReady={isDescriptionEditorReady}
        setQuillHost={setQuillHost}
        onSave={saveDescriptionEditor}
        onClose={closeDescriptionEditor}
      />

      <StartPointDeleteModal
        isOpen={pendingDelete}
        deletingStartPoint={deletingStartPoint}
        onClose={() => {
          if (deletingStartPoint) return;
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <StartPointPromoCodesModal
        isOpen={Boolean(promoCodesPointId)}
        startPointId={
          promoCodesPoint?.uuid ||
          promoCodesPoint?.internalId ||
          promoCodesPoint?.stp_id ||
          promoCodesPoint?.stpId ||
          promoCodesPointId
        }
        pointName={
          normalizeStartPointLocalizedValue(promoCodesPoint?.name?.en) ||
          normalizeStartPointLocalizedValue(
            promoCodesPoint?.name?.[visibleLanguageIso],
          ) ||
          normalizeStartPointLocalizedValue(promoCodesPoint?.name?.es) ||
          promoCodesPointId
        }
        onClose={() => setPromoCodesPointId("")}
      />

      <AppModal
        isOpen={Boolean(additionalInfoPointId)}
        onClose={() => setAdditionalInfoPointId("")}
        title={`Additional description information - ${
          normalizeStartPointLocalizedValue(additionalInfoPoint?.name?.en) ||
          normalizeStartPointLocalizedValue(
            additionalInfoPoint?.name?.[visibleLanguageIso],
          ) ||
          additionalInfoPointId
        }`}
        size="lg"
      >
        <div className="start-point-additional-info-modal">
          <div className="start-point-tool-form">
            <div>
              <p className="tour-add-form-label">name</p>
              <label className="field-label app-form-field">
                <input
                  className="field-input"
                  value={normalizeStartPointLocalizedValue(
                    additionalInfoPoint?.additionalInfo?.name?.[
                      visibleLanguageIso
                    ],
                  )}
                  placeholder={`Write name (${visibleLanguageIso})`}
                  onChange={(event) =>
                    updateAdditionalInfoName(
                      additionalInfoPointId,
                      visibleLanguageIso,
                      event.target.value,
                    )
                  }
                />
              </label>
            </div>

            <div>
              <p className="tour-add-form-label">description</p>
              <button
                type="button"
                className={`field-label app-form-field field-preview-row ${
                  normalizeStartPointHtmlValue(
                    additionalInfoPoint?.additionalInfo?.description?.[
                      visibleLanguageIso
                    ],
                  )
                    ? "filled"
                    : ""
                }`}
                onClick={() =>
                  openDescriptionEditor(
                    additionalInfoPointId,
                    visibleLanguageIso,
                    "additionalInfoDescription",
                  )
                }
              >
                {normalizeStartPointHtmlValue(
                  additionalInfoPoint?.additionalInfo?.description?.[
                    visibleLanguageIso
                  ],
                ) ? (
                  <span
                    className="field-preview-value field-preview-value-html"
                    dangerouslySetInnerHTML={{
                      __html: normalizeStartPointHtmlValue(
                        additionalInfoPoint?.additionalInfo?.description?.[
                          visibleLanguageIso
                        ],
                      ),
                    }}
                  />
                ) : (
                  <span className="field-preview-value">Complete</span>
                )}
              </button>
            </div>

            <MediaPicker
              title="images"
              titleError=""
              items={additionalInfoImages.map((image, index) => ({
                ...image,
                id: image.id || image.url || `additional-info-image-${index}`,
              }))}
              isUploading={Boolean(
                uploadingAdditionalInfoImageByPointId[additionalInfoPointId],
              )}
              uploadError={
                additionalInfoImageErrorByPointId[additionalInfoPointId] || ""
              }
              accept={{ "image/*": [] }}
              maxFiles={Math.max(
                1,
                MAX_ADDITIONAL_INFO_IMAGES - additionalInfoImages.length,
              )}
              invalidTypeMessage="Image only for this section."
              onRejected={(message) =>
                setAdditionalInfoImageErrorByPointId((prev) => ({
                  ...prev,
                  [additionalInfoPointId]: message,
                }))
              }
              onUpload={(files) =>
                handleUploadAdditionalInfoImage(additionalInfoPointId, files)
              }
              onRemove={(imageId) =>
                removeAdditionalInfoImage(additionalInfoPointId, imageId)
              }
              uploadLabel="Upload additional information images"
              uploadHint={`Up to ${MAX_ADDITIONAL_INFO_IMAGES} images are allowed for this section.`}
            />

            <div className="start-point-tool-save-row">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--save"
                aria-label="Save additional information"
                title="Save additional information"
                onClick={() => {
                  setAdditionalInfoPointId("");
                }}
              >
                <IconDeviceFloppy size={18} />
              </button>
            </div>
          </div>
        </div>
      </AppModal>

      <AppModal
        isOpen={isTranslateModalOpen}
        onClose={() => setIsTranslateModalOpen(false)}
        closeDisabled={isTranslating}
        title="AI Translate · Driving Tour Points"
        size="md"
        className="mode-route-translate-modal"
      >
        <p>
          Translate the English names and descriptions of all driving tour
          points, including additional information.
        </p>
        <fieldset
          className="mode-route-translate-languages"
          disabled={isTranslating || translateComplete}
        >
          <legend>Target language</legend>
          {translationLanguages.map((language) => (
            <label key={language.iso}>
              <input
                type="radio"
                name="start-point-translation-language"
                value={language.iso}
                checked={translateLanguage === language.iso}
                onChange={() => setTranslateLanguage(language.iso)}
              />
              <span>{language.name || language.iso.toUpperCase()}</span>
              <small>{language.iso.toUpperCase()}</small>
            </label>
          ))}
        </fieldset>
        {!translationLanguages.length ? (
          <p>No translation languages are available.</p>
        ) : null}
        {isTranslating ? (
          <ThinkingAnimation label="Translating driving tour points..." />
        ) : null}
        {translateProgress.total ? (
          <section
            className={`mode-route-ai-stream ${isTranslating ? "is-running" : "is-finished"}`}
            aria-live="polite"
          >
            <div className="mode-route-ai-stream-heading">
              <strong>
                {translateProgress.done
                  ? "Translation summary"
                  : "Translating driving tour points"}
              </strong>
              <span>
                {translateProgress.completed} / {translateProgress.total} points
              </span>
            </div>
            <div
              className="mode-route-ai-stream-track"
              role="progressbar"
              aria-label="Driving tour points translation progress"
              aria-valuemin={0}
              aria-valuemax={translateProgress.total}
              aria-valuenow={translateProgress.completed}
            >
              <span style={{ width: `${translationPercent}%` }} />
            </div>
            <div className="mode-route-ai-stream-counts">
              <span className="is-completed">
                {translateProgress.points.filter(
                  (point) => point.status === "completed",
                ).length} translated
              </span>
              <span className="is-error">
                {translateProgress.points.filter(
                  (point) => point.status === "error",
                ).length} errors
              </span>
              <span>
                {translateProgress.points.filter(
                  (point) => point.status === "pending",
                ).length} waiting
              </span>
            </div>
            <ol className="mode-route-ai-stream-list">
              {translateProgress.points.map((point, index) => (
                <li key={point.id} className={`is-${point.status}`}>
                  <span className="mode-route-ai-stream-status" aria-hidden="true">
                    {point.status === "completed"
                      ? "✓"
                      : point.status === "error"
                        ? "!"
                        : "…"}
                  </span>
                  <span className="mode-route-ai-stream-point">
                    <strong>#{index + 1} · {point.name}</strong>
                    {point.message ? <small>{point.message}</small> : null}
                  </span>
                  <span className="mode-route-ai-stream-label">
                    {point.status === "completed"
                      ? "Translated"
                      : point.status === "error"
                        ? "Error"
                        : "Waiting"}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        {translateComplete ? (
          <p className="mode-route-translate-success" role="status">
            Translation complete. Review the selected language and save each
            point to persist the changes.
          </p>
        ) : null}
        {translateError ? (
          <p className="message-error" role="alert">{translateError}</p>
        ) : null}
        <div className="tour-add-editor-actions">
          <button
            type="button"
            className="button-ghost"
            onClick={() => setIsTranslateModalOpen(false)}
            disabled={isTranslating}
          >
            {translateProgress.done ? "Close" : "Cancel"}
          </button>
          {!translateComplete ? (
            <button
              type="button"
              className="button-primary"
              onClick={handleTranslateStartPoints}
              disabled={
                !translateLanguage ||
                isTranslating ||
                !translationLanguages.length ||
                !startPoints?.length
              }
            >
              {isTranslating ? "Translating..." : "Translate"}
            </button>
          ) : null}
        </div>
      </AppModal>

      {aiToolsAnchorReady ? (
        <AiToolsPanel
          anchorRef={aiToolsAnchorRef}
          title="AI TOOLS"
          ariaLabel="AI TOOLS"
          isVisible={
            Boolean(activeStartTour) &&
            hasResolvedActiveLanguage &&
            isEnglishLanguage
          }
          actions={[
            {
              id: "translate-start-points",
              label: "AI Translate",
              icon: <IconLanguage size={17} />,
              disabled: !startPoints?.length || isTranslating,
              onClick: openTranslateModal,
            },
          ]}
        />
      ) : null}
    </aside>
  );
}

export default StartPointToolPanel;
